// apps/client/src/components/PageGenerationStatus.tsx
import { JobStatus, QueueType } from '@fsp/types';
import { AccountTreeTwoTone, CheckCircle, Close, Error as ErrorIcon, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueue } from '../contexts/QueueContext';
import { useNavigate } from 'react-router-dom';
import type { FlowchartGenerationJob, PageGenerationJob, QueueJob } from '../contexts/QueueContext';

interface PageGenerationStatusProps {
  sidebarOpen: boolean;
}

export const PageGenerationStatus: React.FC<PageGenerationStatusProps> = ({ sidebarOpen }) => {
  const { pageGenerationJobs, flowchartGenerationJobs, clearPageGeneration, clearFlowchartGeneration } = useQueue();
  const navigate = useNavigate();

  // Combine all jobs and show the first active one
  const allJobs: QueueJob[] = [
    ...pageGenerationJobs,
    ...flowchartGenerationJobs,
  ];

  const currentJob = allJobs.length > 0 ? allJobs[0] : null;

  if (!currentJob) {
    return null;
  }

  const isPageJob = currentJob.queueType === QueueType.PAGE_GENERATION;
  const isFlowchartJob = currentJob.queueType === QueueType.FLOWCHART_GENERATION;

  const status = currentJob.status;
  const errorMessage = currentJob.errorMessage;

  const isInProgress = status === JobStatus.IN_PROGRESS || status === JobStatus.QUEUED;
  const isComplete = status === JobStatus.COMPLETED;
  const isFailed = status === JobStatus.FAILED;

  const handleClear = () => {
    if (isPageJob) {
      clearPageGeneration(currentJob.jobId);
    } else if (isFlowchartJob) {
      clearFlowchartGeneration(currentJob.jobId);
    }
  };

  const handleViewPage = () => {
    if (isPageJob) {
      const job = currentJob as PageGenerationJob;
      clearPageGeneration(job.jobId);
      navigate(`/app/plans/${job.planId}/new/chapters/${job.chapterId}/pages/${job.pageId}/content`);
    }
  };

  const handleViewFlowchart = () => {
    if (isFlowchartJob) {
      const job = currentJob as FlowchartGenerationJob;
      clearFlowchartGeneration(job.jobId);
      navigate(`/app/plans/${job.planId}/new/flow`);
    }
  };

  // Get display text based on job type
  const getJobTitle = () => {
    if (isPageJob) {
      const job = currentJob as PageGenerationJob;
      return job.pageTitle;
    } else if (isFlowchartJob) {
      return 'Product Flow Diagram';
    }
    return 'Unknown';
  };

  const getJobSubtitle = () => {
    if (isPageJob) {
      const job = currentJob as PageGenerationJob;
      return job.chapterTitle;
    }
    return null;
  };

  const getProgressText = () => {
    if (isPageJob) {
      const job = currentJob as PageGenerationJob;
      return `AI is generating "${job.pageTitle}" for ${job.chapterTitle}...`;
    } else if (isFlowchartJob) {
      return 'AI is generating your product flow diagram...';
    }
    return 'Generating...';
  };

  const getHeaderText = () => {
    if (isInProgress) {
      return isFlowchartJob ? 'Generating Flowchart...' : 'Generating Page...';
    } else if (isComplete) {
      return isFlowchartJob ? 'Flowchart Generated!' : 'Page Generated!';
    } else if (isFailed) {
      return 'Generation Failed';
    }
    return '';
  };

  // Collapsed view - just show circular progress centered
  if (!sidebarOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          ml: 2,
        }}
      >
        <Tooltip
          title={
            isComplete
              ? `${getJobTitle()} generated! Click to view`
              : isInProgress
                ? `Generating ${getJobTitle()}...`
                : 'Generation failed'
          }
          placement="right"
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isComplete ? 'success.light' : isFailed ? 'error.light' : 'action.hover',
              cursor: isComplete ? 'pointer' : 'default',
              transition: 'all 0.2s',
              ...(isComplete && {
                '&:hover': {
                  bgcolor: 'success.main',
                },
              }),
            }}
            onClick={isComplete ? (isFlowchartJob ? handleViewFlowchart : handleViewPage) : undefined}
          >
            {isInProgress && <CircularProgress size={24} />}
            {isComplete && <CheckCircle sx={{ fontSize: 24, color: 'success.main' }} />}
            {isFailed && <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // Expanded view - show full details
  return (
    <Box sx={{ px: 1, py: 2 }}>
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {isInProgress && <CircularProgress size={16} />}
              {isComplete && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
              {isFailed && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
              <Typography variant="caption" fontWeight={600}>
                {getHeaderText()}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ ml: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          {/* Progress bar (indeterminate) */}
          {isInProgress && (
            <>
              <LinearProgress variant="indeterminate" sx={{ height: 4, borderRadius: 2 }} />
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                {getProgressText()}
              </Typography>
            </>
          )}

          {/* Success message with button - Page */}
          {isComplete && isPageJob && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Visibility />}
              onClick={handleViewPage}
              fullWidth
              sx={{ mt: 0.5 }}
            >
              Open Page
            </Button>
          )}

          {/* Success message with button - Flowchart */}
          {isComplete && isFlowchartJob && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AccountTreeTwoTone />}
              onClick={handleViewFlowchart}
              fullWidth
              sx={{ mt: 0.5 }}
            >
              Open Flowchart
            </Button>
          )}

          {/* Error message */}
          {isFailed && errorMessage && (
            <Typography variant="caption" color="error.main" fontSize="0.7rem">
              {errorMessage}
            </Typography>
          )}

          {/* Show queue count if more than one */}
          {allJobs.length > 1 && (
            <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
              +{allJobs.length - 1} more in queue
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default PageGenerationStatus;
